import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import BatchUpdateSavingBalanceForm from "../components/BatchUpdateSavingBalanceForm";
import SavingGoalCarousel from "../components/SavingGoalCarousel";

function Savings({ onSavingUpdateSuccess }) {
  const [savingAccounts, setSavingAccounts] = useState([]);
  const [totalSavingBalance, setTotalSavingBalance] = useState(0);
  const { t } = useTranslation();
  const [savingGoals, setSavingGoals] = useState([]);

  const fetchSavingGoals = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/saving-goals`);
    setSavingGoals(response.data);
  };

  const handleSavingGoalUpdate = (updatedGoal) => {
    const newGoals = savingGoals.map((g) => {
      if (g._id === updatedGoal._id) {
        return updatedGoal;
      }
      return g;
    });
    setSavingGoals(newGoals);
    fetchTotalSavingBalance();
  };

  const fetchSavingAccounts = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/saving-accounts`);
    setSavingAccounts(response.data);
  };

  const fetchTotalSavingBalance = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/total-saving-balance`);
    setTotalSavingBalance(response.data.totalSavingBalance);
  };

  useEffect(() => {
    fetchSavingAccounts();
    fetchTotalSavingBalance();
    fetchSavingGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-4xl lg:max-w-5xl mx-auto space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-6">
        <div className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800 rounded-2xl text-white shadow-lg shadow-primary-500/25 dark:shadow-primary-900/30 transform transition-all hover:scale-[1.02]">
          {/* Subtle decorative circles */}
          <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/5 rounded-full" />

          {/* Content */}
          <div className="relative p-6 flex flex-col justify-center items-center text-center">
            <h2 className="text-white/80 text-sm font-medium mb-2">{t("totalSavingBalance")}</h2>
            <p className="text-4xl font-bold font-heading text-white">
              {totalSavingBalance.toFixed(0)} <span className="text-2xl font-normal text-white/70">EUR</span>
            </p>
          </div>
        </div>

        <div className="card bg-white dark:bg-dark-surface p-0 overflow-hidden transition-colors">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-dark-bg transition-colors">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{t("savingGoals")}</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-surface transition-colors">
            <SavingGoalCarousel slides={savingGoals} options={{ loop: true }} onUpdate={handleSavingGoalUpdate} />
          </div>
        </div>
      </div>

      <div>
        <BatchUpdateSavingBalanceForm
          savingAccounts={savingAccounts}
          onSavingUpdateSuccess={() => {
            fetchSavingAccounts();
            fetchTotalSavingBalance();
            onSavingUpdateSuccess();
          }}
        />
      </div>
    </motion.div>
  );
}

export default Savings;
